import { AlertClaim, MessageAlert } from './alertBus';

/**
 * The toasts one tab is showing (T-076). They stay until someone deals with
 * them: closed, cleared, opened, or handled elsewhere — there is no timer.
 * Newest first; one entry per conversation, so a second message merges into
 * the first toast instead of stacking beside it.
 *
 * Plain state with no React or Supabase in it, so the rules can be tested on
 * their own; MessageToast.tsx renders it and useMessageAlerts feeds it.
 */

/** Shown in full; the rest wait behind a "+N more" pill. */
export const TOASTS_VISIBLE = 3;
/** How long "Sylvie replied" stays up before the toast leaves. */
export const HANDLED_HOLD_MS = 1200;
/** The exit animation (index.css `.message-toast[data-leaving]`). */
export const TOAST_EXIT_MS = 300;

export interface ToastEntry {
  alert: MessageAlert;
  /** Set when somebody else dealt with it: "Replied", "Sylvie took it". */
  handled: string | null;
  /** Animating out; already gone as far as anything but the renderer knows. */
  leaving: boolean;
}

export class ToastStore {
  private entries: ToastEntry[] = [];
  private listeners = new Set<() => void>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  /** A new array on every change, so useSyncExternalStore sees it. */
  getSnapshot = (): ToastEntry[] => this.entries;

  /** The toasts still up (not on their way out). */
  live(): ToastEntry[] {
    return this.entries.filter((e) => !e.leaving);
  }

  has(key: string): boolean {
    return this.entries.some((e) => e.alert.key === key && !e.leaving);
  }

  get(key: string): ToastEntry | undefined {
    return this.entries.find((e) => e.alert.key === key);
  }

  /**
   * Show an alert, or merge it into its conversation's toast: the newer alert
   * (count, latest text) replaces the old one and moves to the front. A toast
   * that was leaving comes back.
   */
  show(alert: MessageAlert) {
    this.cancel(alert.key);
    const rest = this.entries.filter((e) => e.alert.key !== alert.key);
    this.set([{ alert, handled: null, leaving: false }, ...rest]);
  }

  claim(key: string, claim: AlertClaim) {
    this.patch(key, (e) => ({ ...e, alert: { ...e.alert, claim } }));
  }

  /** Somebody else dealt with it: say so, hold, then leave. */
  handle(key: string, text: string) {
    const e = this.get(key);
    if (!e || e.leaving || e.handled) return;
    this.patch(key, (x) => ({ ...x, handled: text }));
    this.cancel(key);
    this.timers.set(
      key,
      setTimeout(() => this.remove(key), HANDLED_HOLD_MS),
    );
  }

  /** Take one down (animated). False if it wasn't up. */
  remove(key: string): boolean {
    const e = this.get(key);
    if (!e || e.leaving) return false;
    this.cancel(key);
    this.patch(key, (x) => ({ ...x, leaving: true }));
    this.timers.set(
      key,
      setTimeout(() => {
        this.timers.delete(key);
        this.set(this.entries.filter((x) => x.alert.key !== key));
      }, TOAST_EXIT_MS),
    );
    return true;
  }

  /** Clear all: every toast, including the ones behind "+N more". */
  removeAll() {
    this.live().forEach((e) => this.remove(e.alert.key));
  }

  private cancel(key: string) {
    const t = this.timers.get(key);
    if (t !== undefined) clearTimeout(t);
    this.timers.delete(key);
  }

  private patch(key: string, fn: (e: ToastEntry) => ToastEntry) {
    this.set(this.entries.map((e) => (e.alert.key === key ? fn(e) : e)));
  }

  private set(next: ToastEntry[]) {
    this.entries = next;
    this.listeners.forEach((fn) => fn());
  }
}

/**
 * What the stack draws: the newest three live toasts, plus any that are
 * animating out from among them, and how many wait behind "+N more".
 */
export function stackView(entries: ToastEntry[]): { shown: ToastEntry[]; more: number; live: number } {
  const shown: ToastEntry[] = [];
  let liveShown = 0;
  let live = 0;
  for (const e of entries) {
    if (e.leaving) {
      if (shown.length < TOASTS_VISIBLE) shown.push(e);
      continue;
    }
    live += 1;
    if (liveShown < TOASTS_VISIBLE) {
      shown.push(e);
      liveShown += 1;
    }
  }
  return { shown, more: live - liveShown, live };
}

/**
 * The leader's side of a merge: a message on a conversation whose toast is
 * still up counts on from it and keeps when the run started.
 */
export function mergeAlert(prev: MessageAlert | undefined, next: MessageAlert): MessageAlert {
  if (!prev) return next;
  return {
    ...next,
    count: prev.count + 1,
    firstAt: prev.firstAt,
    // A lead taken while its toast was up stays taken.
    claim: next.claim ?? prev.claim,
  };
}

/** What a toast says as it leaves because somebody dealt with it elsewhere. */
export function handledReason(
  row: { last_message_direction: string | null; snoozed_until: string | null },
  dismissal: { dismissed_by_user_id: string | null; dismissed_by_name: string | null } | undefined,
  meId: string,
  now = Date.now(),
): string {
  if (row.last_message_direction === 'outbound') return 'Replied';
  const who =
    dismissal?.dismissed_by_user_id === meId ? 'You' : dismissal?.dismissed_by_name || null;
  if (row.snoozed_until && new Date(row.snoozed_until).getTime() > now) {
    return who ? `${who} snoozed it` : 'Snoozed';
  }
  return who ? `${who} marked it done` : 'Marked done';
}
